import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Cloud, Calendar, ShoppingCart, 
  AlertTriangle, ArrowUp, ArrowDown, Minus,
  RefreshCw, Download, Settings, Package, Clock
} from 'lucide-react';
import { colors } from '../../../constants/theme';
import { Card } from '../../ui/Card';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';
import ForecastChart from './ForecastChart';
import SKUAnalysis from './SKUAnalysis';
import WeatherImpact from './WeatherImpact';
import EventCalendar from './EventCalendar';
import AgentCommunicationBus from '../../../services/AgentCommunicationBus';

interface ForecastData {
  date: string;
  actual?: number;
  forecast: number;
  lower: number;
  upper: number;
}

interface SKUForecast {
  skuId: string;
  skuName: string;
  category: string;
  currentStock: number;
  forecastDemand: number;
  trend: 'up' | 'down' | 'stable';
  trendPercent: number;
  confidence: number;
}

const DemandForecastAgent: React.FC = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [forecastData, setForecastData] = useState<ForecastData[]>([]);
  const [skuForecasts, setSkuForecasts] = useState<SKUForecast[]>([]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [accuracy, setAccuracy] = useState(94.8);

  // エージェント登録
  useEffect(() => {
    AgentCommunicationBus.register(
      'demand_forecast',
      ['*'], // すべてのトピックを購読
      (message) => {
        console.log('[DemandForecast] Received message:', message);
        
        // 他エージェントからのデータ要求に応答
        if (message.type === 'data_request' && message.topic === 'forecast_data') {
          AgentCommunicationBus.send({
            from: 'demand_forecast',
            to: message.from,
            type: 'data_response',
            topic: 'forecast_data_response',
            payload: {
              requestId: message.id,
              data: {
                forecasts: skuForecasts,
                accuracy: accuracy
              }
            }
          });
        }
      }
    );

    return () => {
      AgentCommunicationBus.unregister('demand_forecast');
    };
  }, [skuForecasts, accuracy]);

  // モックデータの生成
  useEffect(() => {
    generateForecastData();
    generateSKUForecasts();
  }, [selectedPeriod, selectedCategory]);

  // 初回マウント時のデータ生成
  useEffect(() => {
    generateForecastData();
    generateSKUForecasts();
    setAccuracy(92 + Math.random() * 5);
  }, []);

  const generateForecastData = () => {
    const data: ForecastData[] = [];
    const days = selectedPeriod === 'week' ? 7 : selectedPeriod === 'month' ? 30 : 90;
    const baseValue = 100000;
    
    for (let i = 0; i < days; i++) {
      const date = new Date();
      date.setDate(date.getDate() + i);
      const dayOfWeek = date.getDay();
      
      // 週末は売上増加
      const weekendMultiplier = (dayOfWeek === 0 || dayOfWeek === 6) ? 1.3 : 1;
      const randomVariation = 0.9 + Math.random() * 0.2;
      const trendGrowth = 1 + (i * 0.002); // 微増トレンド
      
      const forecast = baseValue * weekendMultiplier * randomVariation * trendGrowth;
      
      data.push({
        date: date.toISOString().split('T')[0],
        actual: i < 0 ? forecast * (0.95 + Math.random() * 0.1) : undefined,
        forecast: Math.round(forecast),
        lower: Math.round(forecast * 0.9),
        upper: Math.round(forecast * 1.1)
      });
    }
    
    setForecastData(data);
  };

  const generateSKUForecasts = () => {
    const categories = ['飲料', '菓子', '日配品', '冷凍食品', '生鮮'];
    const items = [
      { id: 'SKU001', name: 'コカ・コーラ 500ml', category: '飲料' },
      { id: 'SKU002', name: 'ポテトチップス うすしお', category: '菓子' },
      { id: 'SKU003', name: '牛乳 1L', category: '日配品' },
      { id: 'SKU004', name: '冷凍餃子 12個入', category: '冷凍食品' },
      { id: 'SKU005', name: 'バナナ 1房', category: '生鮮' },
      { id: 'SKU006', name: 'サントリー天然水 2L', category: '飲料' },
      { id: 'SKU007', name: 'チョコレート', category: '菓子' },
      { id: 'SKU008', name: 'ヨーグルト 4個パック', category: '日配品' },
    ];
    
    const filtered = selectedCategory === 'all' 
      ? items 
      : items.filter(item => item.category === selectedCategory);
    
    const forecasts: SKUForecast[] = filtered.map(item => {
      const trendRand = Math.random();
      const trend = trendRand > 0.6 ? 'up' : trendRand > 0.3 ? 'stable' : 'down';
      const trendPercent = trend === 'up' ? 5 + Math.random() * 15 : 
                          trend === 'down' ? -(5 + Math.random() * 10) : 
                          Math.random() * 3 - 1.5;
      
      return {
        skuId: item.id,
        skuName: item.name,
        category: item.category,
        currentStock: Math.floor(Math.random() * 500) + 100,
        forecastDemand: Math.floor(Math.random() * 300) + 50,
        trend,
        trendPercent,
        confidence: 75 + Math.random() * 20
      };
    });
    
    setSkuForecasts(forecasts);
  };

  const runAnalysis = () => {
    setIsAnalyzing(true);
    
    // MDカレンダーエージェントにデータを送信
    AgentCommunicationBus.send({
      from: 'demand_forecast',
      to: '52week_md',
      type: 'update',
      topic: 'demand_insights',
      payload: {
        highDemandWeeks: [12, 18, 25, 38, 45, 51], // 需要が高い週
        lowDemandWeeks: [3, 8, 15, 30, 35],       // 需要が低い週
        seasonalTrends: {
          spring: { trend: 'up', percent: 8 },
          summer: { trend: 'up', percent: 15 },
          autumn: { trend: 'stable', percent: 2 },
          winter: { trend: 'down', percent: -5 }
        }
      },
      priority: 'high'
    });
    
    // 在庫最適化エージェントに予測数量を送信
    AgentCommunicationBus.send({
      from: 'demand_forecast',
      to: 'inventory',
      type: 'data_response',
      topic: 'forecast_quantities',
      payload: {
        forecasts: skuForecasts.map(sku => ({
          skuId: sku.skuId,
          quantity: sku.forecastDemand,
          confidence: sku.confidence
        }))
      },
      priority: 'high'
    });
    
    setTimeout(() => {
      setIsAnalyzing(false);
      const newAccuracy = 92 + Math.random() * 5;
      setAccuracy(newAccuracy);
      
      // 予測実行アクション
      console.log('🔮 需要予測を実行しました！', {
        period: selectedPeriod,
        category: selectedCategory,
        accuracy: newAccuracy,
        forecasts: skuForecasts.length
      });
      alert(`✅ 需要予測を実行しました！\n\n予測結果:\n- 期間: ${selectedPeriod === 'week' ? '1週間' : selectedPeriod === 'month' ? '1ヶ月' : '3ヶ月'}\n- カテゴリ: ${selectedCategory === 'all' ? '全カテゴリ' : selectedCategory}\n- 予測精度: ${newAccuracy.toFixed(1)}%\n- 予測SKU数: ${skuForecasts.length}件\n- 上昇トレンド: ${skuForecasts.filter(s => s.trend === 'up').length}件`);
    }, 2000);
  };

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="p-1.5">
        {/* 超コンパクトヘッダー */}
        <div className="mb-1">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1">
              <TrendingUp size={16} style={{ color: colors.accent.cyan }} />
              <h2 className="text-lg font-bold" style={{ color: colors.text.primary }}>
                需要予測エージェント
              </h2>
              <Badge variant="default" size="sm">高精度AI予測</Badge>
            </div>
            <div className="flex gap-1">
              <select 
                className="px-1 py-0.5 text-xs border rounded text-[10px]" 
                style={{ borderColor: colors.ui.border }}
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
              >
                <option value="week">1週間</option>
                <option value="month">1ヶ月</option>
                <option value="quarter">3ヶ月</option>
              </select>
              <select 
                className="px-1 py-0.5 text-xs border rounded text-[10px]" 
                style={{ borderColor: colors.ui.border }}
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">全カテゴリ</option>
                <option value="飲料">飲料</option>
                <option value="菓子">菓子</option>
                <option value="日配品">日配品</option>
              </select>
              <Button variant="primary" size="small" onClick={runAnalysis} disabled={isAnalyzing}>
                <RefreshCw size={10} className={isAnalyzing ? 'animate-spin' : ''} />
                <span className="text-[10px]">{isAnalyzing ? '分析中...' : '予測実行'}</span>
              </Button>
            </div>
          </div>
        </div>

        {/* 超コンパクトKPIダッシュボード */}
        <div className="grid grid-cols-6 gap-0.5 mb-1">
          <MiniKPICard
            label="予測精度"
            value={`${accuracy.toFixed(1)}%`}
            change="+2.3%"
            trend="up"
          />
          <MiniKPICard
            label="異常検知"
            value="3件"
            change="要確認"
            trend="warn"
          />
          <MiniKPICard
            label="上昇トレンド"
            value={`${skuForecasts.filter(s => s.trend === 'up').length}品`}
            change="50%"
            trend="up"
          />
          <MiniKPICard
            label="在庫リスク"
            value={`${skuForecasts.filter(s => s.currentStock < s.forecastDemand * 0.3).length}品`}
            change="要発注"
            trend="down"
          />
          <MiniKPICard
            label="天気影響"
            value="高"
            change="雨予報"
            trend="warn"
          />
          <MiniKPICard
            label="イベント"
            value="2件"
            change="今週"
            trend="neutral"
          />
        </div>

        {/* メインコンテンツ - 全情報を1つのコンポーネントに統合 */}
        <div className="space-y-3">
          {/* 全体需要予測チャート */}
          <Card className="p-3">
            <h3 className="text-base font-semibold mb-2" style={{ color: colors.text.primary }}>
              全体需要予測
            </h3>
            <ForecastChart data={forecastData} />
          </Card>
          
          {/* 主要SKU予測 */}
          <Card className="p-3">
            <h3 className="text-base font-semibold mb-2" style={{ color: colors.text.primary }}>
              主要SKU予測
            </h3>
            <div className="overflow-auto" style={{ maxHeight: '300px' }}>
              <table className="min-w-full divide-y text-sm" style={{ borderColor: colors.ui.border }}>
                <thead style={{ backgroundColor: colors.ui.bgSecondary }}>
                  <tr>
                    <th className="p-2 text-left font-medium text-gray-500">SKU</th>
                    <th className="p-2 text-center font-medium text-gray-500">予測</th>
                    <th className="p-2 text-center font-medium text-gray-500">トレンド</th>
                    <th className="p-2 text-center font-medium text-gray-500">信頼度</th>
                  </tr>
                </thead>
                <tbody style={{ backgroundColor: colors.ui.bg }}>
                  {skuForecasts.slice(0, 10).map(sku => (
                    <tr key={sku.skuId}>
                      <td className="p-2 text-left font-medium truncate max-w-[150px]" style={{ color: colors.text.primary }}>
                        {sku.skuName}
                      </td>
                      <td className="p-2 text-center" style={{ color: colors.text.primary }}>
                        {sku.forecastDemand}
                      </td>
                      <td className="p-2 text-center">
                        {sku.trend === 'up' && <ArrowUp size={14} color={colors.ui.success} />}
                        {sku.trend === 'down' && <ArrowDown size={14} color={colors.status.error} />}
                        {sku.trend === 'stable' && <Minus size={14} color={colors.text.secondary} />}
                      </td>
                      <td className="p-2 text-center" style={{ color: colors.text.secondary }}>
                        {sku.confidence.toFixed(1)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* 天気影響分析 */}
          <Card className="p-3">
            <h3 className="text-base font-semibold mb-2" style={{ color: colors.text.primary }}>
              天気影響分析
            </h3>
            <WeatherImpact />
          </Card>
          
          {/* イベントカレンダー */}
          <Card className="p-3">
            <h3 className="text-base font-semibold mb-2" style={{ color: colors.text.primary }}>
              イベントカレンダー
            </h3>
            <EventCalendar />
          </Card>
        </div>

        {/* 詳細分析サマリー */}
        <Card className="p-3 mt-3">
          <h3 className="text-base font-semibold mb-3" style={{ color: colors.text.primary }}>
            AI分析サマリー
          </h3>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div className="p-3 bg-blue-50 rounded">
              <div className="font-medium text-blue-700">週末需要</div>
              <div className="text-blue-600 mt-1">+30% 増加予測</div>
              <div className="text-gray-600 text-xs mt-1">金曜夜〜日曜</div>
            </div>
            <div className="p-3 bg-orange-50 rounded">
              <div className="font-medium text-orange-700">要注意商品</div>
              <div className="text-orange-600 mt-1">3品目が欠品リスク</div>
              <div className="text-gray-600 text-xs mt-1">即時発注推奨</div>
            </div>
            <div className="p-3 bg-green-50 rounded">
              <div className="font-medium text-green-700">好調カテゴリ</div>
              <div className="text-green-600 mt-1">飲料が+15%</div>
              <div className="text-gray-600 text-xs mt-1">気温上昇の影響</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// 超コンパクトKPIカードコンポーネント
interface MiniKPICardProps {
  label: string;
  value: string;
  change: string;
  trend: 'up' | 'down' | 'neutral' | 'warn';
}

const MiniKPICard: React.FC<MiniKPICardProps> = ({ label, value, change, trend }) => {
  const getTrendColor = () => {
    switch(trend) {
      case 'up':
        return colors.ui.success;
      case 'down':
        return colors.status.error;
      case 'warn':
        return colors.status.warning;
      default:
        return colors.text.secondary;
    }
  };
  
  return (
    <Card className="p-1">
      <div className="text-xs truncate" style={{ color: colors.text.secondary }}>
        {label}
      </div>
      <div className="text-sm font-bold truncate" style={{ color: colors.text.primary }}>
        {value}
      </div>
      <div className="text-xs truncate" style={{ color: getTrendColor() }}>
        {change}
      </div>
    </Card>
  );
};

export default DemandForecastAgent;